import React, { useState, useRef, useCallback, useEffect } from "react";

/**
 * Module-level cache of image src URLs that have already fully loaded at least
 * once during this session. Once an image has loaded, any later remount or
 * src re-evaluation (e.g. selecting an element merges a fresh object, or a
 * carousel re-mounts a page) starts in the loaded state instead of briefly
 * flashing the blurred placeholder back over the image — which previously read
 * as a dark "shadow" over the picture on hover/select.
 */
const loadedSrcCache = new Set();

/**
 * Drop-in <img> replacement that shows a rounded placeholder with a shimmer
 * light-sweep while the image loads, then cross-fades to the real image.
 *
 * Props (all standard <img> props pass through):
 * - imgRef: forwarded ref to the underlying <img> element
 * - onLoad: passthrough — still called after the image loads
 * - wrapperStyle: optional inline styles for the positioning wrapper
 * - wrapperClassName: optional classes for the positioning wrapper
 */
const ProgressiveImage = React.forwardRef(({
  src,
  alt,
  style,
  className,
  onLoad: onLoadProp,
  wrapperStyle,
  wrapperClassName = "",
  ...rest
}, forwardedRef) => {
  const [loaded, setLoaded] = useState(() => (src ? loadedSrcCache.has(src) : false));
  const internalRef = useRef(null);
  const imgRef = forwardedRef || internalRef;

  useEffect(() => {
    // Already loaded this URL before — keep it visible, never flash the placeholder.
    if (src && loadedSrcCache.has(src)) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
    const el = typeof imgRef !== "function" ? imgRef?.current : null;
    if (el && el.complete && el.naturalWidth > 0 && el.src) {
      if (src) loadedSrcCache.add(src);
      setLoaded(true);
    }
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoad = useCallback(
    (e) => {
      if (src) loadedSrcCache.add(src);
      setLoaded(true);
      if (onLoadProp) onLoadProp(e);
    },
    [onLoadProp, src]
  );

  const handleError = useCallback(() => {
    setLoaded(true);
  }, []);

  return (
    <span
      className={`progressive-image-wrapper ${wrapperClassName}`}
      style={{
        position: "relative",
        display: "inline-block",
        lineHeight: 0,
        width: style?.width || "100%",
        height: style?.height || "auto",
        borderRadius: "8px",
        overflow: "hidden",
        ...wrapperStyle,
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        style={{
          ...style,
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.35s ease-in-out",
        }}
        className={className}
        onLoad={handleLoad}
        onError={handleError}
        {...rest}
      />
      {src && (
        <span
          className="progressive-image-placeholder"
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(200, 200, 200, 0.15)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            borderRadius: "inherit",
            overflow: "hidden",
            pointerEvents: "none",
            opacity: loaded ? 0 : 1,
            transition: "opacity 0.3s ease-out",
          }}
          aria-hidden="true"
        >
          <span
            className="progressive-image-shimmer"
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
              animation: "progressive-img-shimmer 1.5s ease-in-out infinite",
            }}
          />
        </span>
      )}
    </span>
  );
});

ProgressiveImage.displayName = "ProgressiveImage";

export default ProgressiveImage;
